/**
 * 세션이 끝나면 함께 비워지는 캐시들 — **한 자리에 모아 둔다** (2026-08-31).
 *
 * ── 왜 생겼나 ────────────────────────────────────────────────────────────
 * ★★★ **로그아웃해도 앞 사람의 값이 화면에 남아 있었다.** 사용자 실측(2026-08-31):
 *    「테스트팀장으로 로그인 후 로그아웃하고 테스트 사원으로 로그인하니 **이사 · 테스트**로
 *    보였다」 — 그리고 그 반대 순서로 하면 **사원 · 테스트**로 보였다. 두 방향 다 났다.
 *
 *    프로필 팝오버가 신상을 **「최초 1회」 모듈 변수**에 담아 두는데, 그 변수는 로그아웃을
 *    듣지 못한다. SPA 라 로그아웃해도 **페이지가 다시 뜨지 않기 때문**이다
 *    (`clearSession` 은 상태만 바꾸고, AuthGuard 가 `/login` 으로 «화면만» 옮긴다).
 *    그래서 이름은 새 사람 것(authStore 에서 새로 온다)인데 **직위·소속·사번·연차는 앞 사람 것**이
 *    남아, 한 화면에 두 사람이 섞여 보였다.
 *
 * ★★ **이름이 맞으니 더 나빴다.** 「테스트 / 이사 · 테스트」처럼 사람 이름은 제대로 뜨니
 *    화면을 의심하지 않고 **그 사람의 직위가 이사인 줄로 읽게 된다.**
 *
 * ★★ 프로필만의 일이 아니다. 같은 모양의 모듈 캐시가 여럿이고, 그중에는
 *    **「내가 전사 근태를 볼 수 있나」**(`my-org-scope`)처럼 **메뉴가 서고 안 서고를 정하는 것**도
 *    있다 — 담당자가 로그아웃한 자리에 직원이 로그인하면 「근태 관리」가 그대로 남는다.
 *
 * ── 어떻게 ───────────────────────────────────────────────────────────────
 * 캐시를 가진 모듈이 **자기 비우는 법을 등록**하고, 세션이 지워질 때 한 번에 부른다.
 * 각 모듈이 저마다 로그아웃을 구독하면 «구독을 빠뜨린 모듈»이 생기고, 그것이 지금 상태다.
 *
 * ★ **로그아웃 때 새로고침하는 방법은 쓰지 않는다.** `clearSession` 은 401 에서도 불리므로
 *   (`api/client` 의 인터셉터), 거기서 새로고침하면 **다시 401 → 다시 새로고침** 고리가 된다.
 * ★ 하나가 실패해도 나머지는 비운다 — 비우다 만 상태가 제일 나쁘다.
 * ★ 등록은 **모듈이 적재될 때** 일어난다. 캐시에 값이 들어가려면 그 모듈이 이미 적재돼
 *   있어야 하므로, 「값은 있는데 등록이 없는」 창은 생기지 않는다.
 */
type Reset = () => void;

const resets = new Set<Reset>();

/** 이 캐시는 세션이 끝나면 비운다 — 모듈 맨 아래에서 한 번 부른다. */
export function registerSessionCache(reset: Reset): void {
  resets.add(reset);
}

/** 세션 종료 — 등록된 캐시를 모두 비운다. `authStore.clearSession()` 이 부른다. */
export function clearSessionCaches(): void {
  for (const reset of resets) {
    try {
      reset();
    } catch {
      /* 하나가 실패해도 나머지는 비운다 — 비우다 만 상태가 제일 나쁘다. */
    }
  }
}

/** 검사 전용 — 등록된 캐시 수(모듈이 실제로 등록했는지 확인용). */
export function _registeredSessionCacheCount(): number {
  return resets.size;
}
